/**
 * ClesApiAgents — les clés d'API des agents IBM i (page Synchronisation, 2026-09-16)
 *
 * Une clé `svk_` par dossier : l'agent ServUp s'en sert pour lire sa file
 * (`queue.poll`) et pousser le CDC (`cdc.push`, POST /api/agent/events/ingest).
 * Modèle Pimer (APIKeysPage) : la clé en clair n'est affichée qu'UNE fois, avec
 * les identifiants et la commande du kit à coller en PASE. Routes : GET/POST/PATCH
 * /api/api-keys (L1.7), bornées au périmètre de l'appelant.
 *   - cdc.view   : voir la liste ;
 *   - cdc.deploy : générer une clé pour l'établissement courant, révoquer.
 * La clé n'est jamais journalisée ni stockée côté navigateur.
 *
 * API publique v1 (2026-09-17) : la même clé peut porter api.read / api.ingest /
 * api.changes (gestion commerciale tierce, /api/v1, documentation à /api/v1/docs).
 * Pour une organisation autonome (sans IBM i — `contexte.organisation_mode` rendu par
 * GET /api/api-keys), l'écran ne propose que ces droits-là et remplace les commandes
 * du kit par un exemple d'appel.
 *
 * Admins d'un compte (décision du 17/09) : la permission api.manage suffit pour voir,
 * générer et révoquer les clés de l'API publique ; les droits d'agent (et les clés
 * d'agent) restent à cdc.view / cdc.deploy.
 */

import { useState, useEffect, useCallback } from 'react';
import { KeyIcon, RefreshIcon, CheckIcon, AlertTriangleIcon } from '../../icons';
import { useHasPermission, useUser } from '../../SupabaseWrapper';

interface CleApi {
  id: string;
  establishment_id: string;
  organization_id: string;
  dossier: string | null;
  key_prefix: string;
  scopes: string[];
  expires_at: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  /** Rendu par GET : l'appelant peut-il la révoquer (même règle que PATCH) ? */
  gerable?: boolean;
}

interface CleCreee extends CleApi {
  plain_key: string;
}

const API_BASE = window.location.origin;
const DUREE_MAX_JOURS = 366;
const DUREE_DEFAUT_JOURS = 90;

type GroupeScope = 'agent' | 'api';
export const SCOPES: { code: string; libelle: string; aide: string; groupe: GroupeScope }[] = [
  { code: 'queue.poll', libelle: 'Lire la file (poll)', aide: 'lectures et écritures demandées par ServUp', groupe: 'agent' },
  { code: 'cdc.push', libelle: 'Pousser le CDC', aide: 'événements de SUPEVTLOG vers ServUp', groupe: 'agent' },
  { code: 'api.read', libelle: 'Lire', aide: 'ordres, statuts, techniciens (GET /api/v1/…)', groupe: 'api' },
  { code: 'api.ingest', libelle: 'Écrire', aide: 'ordres, statuts, techniciens, articles (POST /api/v1/ingest/…)', groupe: 'api' },
  { code: 'api.changes', libelle: 'Suivre le terrain', aide: 'flux des changements (GET /api/v1/changes)', groupe: 'api' },
];
const LIBELLE_GROUPE: Record<GroupeScope, string> = { agent: 'Agent IBM i', api: 'API publique' };

/** Droits cochés d'office : ceux de l'agent pour un site IBM i, l'API complète pour une organisation autonome. */
export function scopesParDefaut(usage: 'agent' | 'api'): string[] {
  return SCOPES.filter((s) => s.groupe === usage).map((s) => s.code);
}

/** Exemple d'appel de l'API publique : la clé passe par une variable d'environnement. */
export function exempleAppelApi(o: { cle: string; url: string }): string {
  return [
    `export SERVUP_API_KEY='${o.cle}'`,
    `curl -s -H "X-API-Key: $SERVUP_API_KEY" ${o.url}/api/v1/whoami`,
  ].join('\n');
}

/** 'AAAA-MM-JJ' à J + n (heure locale), pour un <input type="date">. */
function dateDansJours(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const jj = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${jj}`;
}

/** État lisible d'une clé. */
function etatCle(c: CleApi): { libelle: string; couleur: string; fond: string } {
  if (!c.is_active || c.revoked_at) return { libelle: 'Révoquée', couleur: '#991b1b', fond: '#fee2e2' };
  if (Date.parse(c.expires_at) <= Date.now()) return { libelle: 'Expirée', couleur: '#92400e', fond: '#fef3c7' };
  return { libelle: 'Active', couleur: '#166534', fond: '#dcfce7' };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Nom du fichier de clé sur la box, par dossier (jamais la clé dans argv : --cle-fichier). */
const fichierCle = (dossier: string) => `$HOME/.servup-cle-${dossier.toLowerCase()}`;

/** Les commandes du kit à coller en PASE, dans /opt/servup-provisioning. */
export function commandesProvision(o: { cle: string; dossier: string; etablissementId: string; url: string }): string {
  const f = fichierCle(o.dossier);
  const http = /^http:\/\//i.test(o.url) ? ' \\\n  --autoriser-http' : '';
  return [
    'cd /opt/servup-provisioning',
    `printf '%s' '${o.cle}' > "${f}" && chmod 600 "${f}"`,
    `./deploy/agent-provision.sh --dossier ${o.dossier} \\`,
    `  --cle-fichier "${f}" \\`,
    `  --etablissement ${o.etablissementId} \\`,
    `  --url ${o.url}${http}`,
    `rm -f "${f}"`,
  ].join('\n');
}

async function copier(texte: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(texte);
  } catch {
    const zone = document.createElement('textarea');
    zone.value = texte; zone.style.position = 'fixed'; zone.style.opacity = '0';
    document.body.appendChild(zone); zone.select(); document.execCommand('copy'); document.body.removeChild(zone);
  }
}

function ClesApiAgents() {
  const user = useUser();
  const voitAgents = useHasPermission('cdc.view');
  const delivreAgents = useHasPermission('cdc.deploy');
  const gereApi = useHasPermission('api.manage');
  const peutVoir = voitAgents || gereApi;
  const peutGerer = delivreAgents || gereApi;

  const [cles, setCles] = useState<CleApi[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [modeOrganisation, setModeOrganisation] = useState<string | null>(null);
  const siteIbmi = modeOrganisation !== null && modeOrganisation !== 'standalone';
  // « agent » : un site IBM i vu par qui voit les clés d'agent (titre historique) ; sinon « api ».
  const usage: 'agent' | 'api' = modeOrganisation !== 'standalone' && voitAgents ? 'agent' : 'api';
  // Les droits d'agent ne se proposent qu'à qui peut les délivrer, et jamais sans IBM i.
  const proposeAgents = modeOrganisation !== 'standalone' && delivreAgents;
  const [scopes, setScopes] = useState<string[]>(scopesParDefaut('agent'));
  const scopesProposes = SCOPES.filter((s) => proposeAgents || s.groupe === 'api');
  // Cochés d'office : les droits d'agent si on les propose ; sinon l'API — en lecture seule sur
  // un site IBM i (une écriture y répond 409 SOURCE_IBMI).
  useEffect(() => {
    if (proposeAgents) setScopes(scopesParDefaut('agent'));
    else if (siteIbmi) setScopes(['api.read', 'api.changes']);
    else setScopes(scopesParDefaut('api'));
  }, [proposeAgents, siteIbmi]);
  const [expiration, setExpiration] = useState<string>(dateDansJours(DUREE_DEFAUT_JOURS));
  const [urlServeur, setUrlServeur] = useState<string>(API_BASE);
  const [creation, setCreation] = useState(false);
  const [cleCreee, setCleCreee] = useState<CleCreee | null>(null);
  const [copie, setCopie] = useState<string>('');
  const [aRevoquer, setARevoquer] = useState<string | null>(null);

  const jeton = () => localStorage.getItem('sso_token');

  const charger = useCallback(async () => {
    const t = localStorage.getItem('sso_token');
    if (!t) return;
    setChargement(true);
    try {
      const r = await fetch(`${API_BASE}/api/api-keys`, { headers: { Authorization: `Bearer ${t}` } });
      const corps = await r.json().catch(() => ({}));
      if (!r.ok || !corps.success) throw new Error(corps?.error?.message || `HTTP ${r.status}`);
      setCles(corps.data || []);
      setModeOrganisation(corps.contexte?.organisation_mode ?? null);
      setErreur(null);
    } catch (e) {
      console.error('[ClesApi] liste :', e instanceof Error ? e.message : e);
      setErreur('Lecture des clés impossible.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { if (peutVoir) charger(); }, [peutVoir, charger]);

  if (!peutVoir) return null;

  const dossier = user?.dossier || '';
  const etablissementId = user?.establishment_id || '';

  const marquerCopie = (quoi: string) => { setCopie(quoi); setTimeout(() => setCopie(''), 2000); };

  const generer = async () => {
    setErreur(null);
    if (!etablissementId) { setErreur('Aucun établissement courant : choisissez-en un dans le sélecteur.'); return; }
    if (scopes.length === 0) { setErreur('Cochez au moins un droit.'); return; }
    if (!expiration) { setErreur('La date d’expiration est obligatoire.'); return; }
    // Fin de journée locale du jour choisi, envoyée en ISO.
    const fin = new Date(`${expiration}T23:59:00`);
    if (!Number.isFinite(fin.getTime()) || fin.getTime() <= Date.now()) { setErreur('La date d’expiration doit être dans le futur.'); return; }
    setCreation(true);
    try {
      const r = await fetch(`${API_BASE}/api/api-keys`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jeton()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ establishment_id: etablissementId, scopes, expires_at: fin.toISOString() }),
      });
      const corps = await r.json().catch(() => ({}));
      if (!r.ok || !corps.success) throw new Error(corps?.error?.message || `HTTP ${r.status}`);
      setCleCreee(corps.data as CleCreee);
      await charger();
    } catch (e) {
      console.error('[ClesApi] création :', e instanceof Error ? e.message : e);
      setErreur(`Génération impossible : ${e instanceof Error ? e.message : 'erreur inconnue'}`);
    } finally {
      setCreation(false);
    }
  };

  const revoquer = async (id: string) => {
    setErreur(null);
    try {
      const r = await fetch(`${API_BASE}/api/api-keys/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${jeton()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ revoke: true }),
      });
      const corps = await r.json().catch(() => ({}));
      if (!r.ok || !corps.success) throw new Error(corps?.error?.message || `HTTP ${r.status}`);
      setARevoquer(null);
      await charger();
    } catch (e) {
      console.error('[ClesApi] révocation :', e instanceof Error ? e.message : e);
      setErreur('Révocation impossible.');
    }
  };

  const bouton = (actif = true) => ({
    padding: '8px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600 as const,
    cursor: actif ? 'pointer' : 'not-allowed', backgroundColor: actif ? '#0ea5e9' : '#bae6fd', color: 'white',
  });
  const petitBouton = { padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#334155', fontSize: '12px', cursor: 'pointer' };
  const code = { fontFamily: 'monospace', fontSize: '12px', color: '#0f172a', wordBreak: 'break-all' as const };

  const urlPropre = urlServeur.trim().replace(/\/+$/, '');
  // Une clé qui porte un droit d'agent se pose sur la box ; une clé d'API seule s'utilise depuis la gestion commerciale.
  const cleAgent = !!cleCreee && cleCreee.scopes.some((x) => x === 'queue.poll' || x === 'cdc.push');
  const commandes = cleCreee
    ? (cleAgent
      ? commandesProvision({ cle: cleCreee.plain_key, dossier: cleCreee.dossier || dossier, etablissementId: cleCreee.establishment_id, url: urlPropre })
      : exempleAppelApi({ cle: cleCreee.plain_key, url: urlPropre }))
    : '';

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '20px' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <KeyIcon size={20} color="#0ea5e9" />
        <span style={{ fontWeight: 600, color: '#1e293b' }}>{usage === 'api' ? 'Clés de l’API publique' : 'Clés API des agents IBM i'}</span>
        <a href={`${API_BASE}/api/v1/docs`} target="_blank" rel="noreferrer" data-testid="lien-doc-api"
          style={{ fontSize: '12px', color: '#0284c7', textDecoration: 'none' }}>Documentation de l’API ↗</a>
        <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: 'auto', padding: '2px 8px', backgroundColor: '#f1f5f9', borderRadius: '10px' }}>
          {cles.length} clé(s)
        </span>
        <button onClick={charger} title="Actualiser" style={{ ...petitBouton, display: 'flex', alignItems: 'center' }}>
          <RefreshIcon size={14} color="#475569" />
        </button>
      </div>

      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {erreur && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '13px' }}>
            <AlertTriangleIcon size={16} color="#dc2626" />{erreur}
          </div>
        )}

        {/* Identifiants de l'établissement courant : à reporter dans la configuration de l'agent */}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', backgroundColor: '#f8fafc' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Établissement courant</div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', color: '#475569' }}>
            <span>Dossier <strong style={{ color: '#0f172a' }}>{dossier || '—'}</strong></span>
            <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              ID <code style={code}>{etablissementId || '—'}</code>
              {etablissementId && (
                <button style={petitBouton} onClick={async () => { await copier(etablissementId); marquerCopie('etab'); }}>
                  {copie === 'etab' ? 'Copié ✓' : 'Copier'}
                </button>
              )}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
            L’identifiant d’établissement n’est pas un secret. Pour un autre dossier, changez d’établissement dans le sélecteur.
          </div>
        </div>

        {/* Clé fraîchement générée : affichée UNE fois */}
        {cleCreee && (
          <div data-testid="cle-generee" style={{ border: '2px solid #f59e0b', borderRadius: '10px', padding: '14px', backgroundColor: '#fffbeb', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#92400e', fontWeight: 600, fontSize: '13px' }}>
              <AlertTriangleIcon size={16} color="#d97706" />
              Clé du dossier {cleCreee.dossier} — affichée une seule fois : copiez-la maintenant.
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ ...code, fontSize: '14px', padding: '6px 10px', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '6px' }}>{cleCreee.plain_key}</code>
              <button style={petitBouton} onClick={async () => { await copier(cleCreee.plain_key); marquerCopie('cle'); }}>
                {copie === 'cle' ? 'Copiée ✓' : 'Copier la clé'}
              </button>
            </div>
            <label style={{ fontSize: '12px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {cleAgent ? 'URL du serveur ServUp, telle que la box la joint' : 'URL du serveur ServUp, telle que la gestion commerciale la joint'}
              <input value={urlServeur} onChange={(e) => setUrlServeur(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace' }} />
            </label>
            {cleAgent ? (
              <div style={{ fontSize: '12px', color: '#475569' }}>
                À coller dans un shell PASE sur la box — la clé passe par un fichier en 600, jamais par la ligne de commande
                {/^http:\/\//i.test(urlServeur) ? ' ; URL en http : réservé au banc (--autoriser-http)' : ''} :
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#475569' }}>
                Premier appel, pour vérifier la clé (périmètre, droits, débit). La suite est dans la documentation de l’API :
              </div>
            )}
            <pre data-testid="commandes-provision" style={{ margin: 0, padding: '10px 12px', backgroundColor: '#0f172a', color: '#e2e8f0', borderRadius: '8px', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{commandes}</pre>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button style={petitBouton} onClick={async () => { await copier(commandes); marquerCopie('cmd'); }}>
                {cleAgent ? (copie === 'cmd' ? 'Commandes copiées ✓' : 'Copier les commandes') : (copie === 'cmd' ? 'Exemple copié ✓' : 'Copier l’exemple')}
              </button>
              <button style={{ ...petitBouton, marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }} onClick={() => setCleCreee(null)}>
                <CheckIcon size={14} color="#16a34a" /> J’ai copié la clé
              </button>
            </div>
          </div>
        )}

        {/* Génération */}
        {peutGerer && !cleCreee && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Générer une clé pour {dossier || 'l’établissement courant'}</div>
            {(['agent', 'api'] as GroupeScope[]).filter((g) => scopesProposes.some((s) => s.groupe === g)).map((g) => (
              <div key={g} data-groupe-scopes={g} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>{LIBELLE_GROUPE[g]}</div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {scopesProposes.filter((s) => s.groupe === g).map((s) => (
                    <label key={s.code} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                      <input type="checkbox" data-scope={s.code} checked={scopes.includes(s.code)}
                        onChange={(e) => setScopes((prev) => (e.target.checked ? [...prev, s.code] : prev.filter((x) => x !== s.code)))} />
                      <span>{s.libelle} <code style={{ ...code, color: '#64748b' }}>{s.code}</code><br /><span style={{ fontSize: '11px', color: '#94a3b8' }}>{s.aide}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '12px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                Expire le (obligatoire, {DUREE_MAX_JOURS} jours au plus)
                <input type="date" value={expiration} min={dateDansJours(1)} max={dateDansJours(DUREE_MAX_JOURS)}
                  onChange={(e) => setExpiration(e.target.value)}
                  style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
              </label>
              <button style={bouton(!creation && !!etablissementId)} disabled={creation || !etablissementId} onClick={generer}>
                {creation ? 'Génération…' : 'Générer la clé'}
              </button>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              {proposeAgents
                ? 'Plusieurs clés actives sur un même dossier sont admises (rotation) : révoquez l’ancienne une fois la nouvelle posée sur la box. Les droits de l’API publique se cochent à part : sur un site IBM i, ils ne servent qu’à lire (l’écriture y est refusée, l’IBM i fait foi).'
                : siteIbmi
                  ? 'Site relié à un IBM i : l’API publique n’y sert qu’à lire (api.read, api.changes) ; une écriture est refusée, l’IBM i fait foi. Plusieurs clés actives sont admises (rotation).'
                  : 'Plusieurs clés actives sont admises (rotation) : révoquez l’ancienne une fois la nouvelle en service dans la gestion commerciale.'}
            </div>
          </div>
        )}

        {/* Liste */}
        {chargement ? (
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>Chargement…</div>
        ) : cles.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '8px' }}>Aucune clé</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="liste-cles" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  {['Dossier', 'Préfixe', 'Droits', 'Expire le', 'Dernier usage', 'État', ''].map((h) => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '12px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cles.map((c) => {
                  const etat = etatCle(c);
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1e293b' }}>{c.dossier || '—'}</td>
                      <td style={{ padding: '8px 10px' }}><code style={code}>{c.key_prefix}…</code></td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{c.scopes.length ? c.scopes.join(', ') : '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(c.expires_at)}</td>
                      <td style={{ padding: '8px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(c.last_used_at)}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, color: etat.couleur, backgroundColor: etat.fond }}>{etat.libelle}</span>
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {(c.gerable ?? peutGerer) && etat.libelle === 'Active' && (aRevoquer === c.id ? (
                          <span style={{ display: 'flex', gap: '6px' }}>
                            <button style={{ ...petitBouton, borderColor: '#fca5a5', color: '#b91c1c' }} onClick={() => revoquer(c.id)}>Confirmer la révocation</button>
                            <button style={petitBouton} onClick={() => setARevoquer(null)}>Annuler</button>
                          </span>
                        ) : (
                          <button style={petitBouton} onClick={() => setARevoquer(c.id)}>Révoquer</button>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClesApiAgents;
